import * as models from 'smg/customer-api/models';
import { ReportValidity } from 'smg/form';
import * as blockchyp from 'smg/blockchyp';
import * as template from 'smg/template';

import { PlusCardFieldStyle, BlockChypFieldRing, AlternatePaymentNames } from '../../../blockchyp-card';
import { State } from '../../state';
import { Payment } from '../../types';
import { SyncCardIcon } from '../inputs';
import { ResolveMerchantToken } from '../merchant';

import { BindBackLink, EnsureCreditCard, PromoteFormBodyIDs, SetupBillingAddress } from './shared';

// RenderCreditForm replaces $el with the new-credit-card form, mounts the
// BlockChyp PAN/CVC iframes (PCI SAQ A — the digits never reach our origin),
// and wires the still-native MM/YY expiration into state.payment.creditCard.
export function RenderCreditForm(state: State, $el: HTMLElement, back: () => void): void {
    $el.replaceChildren(template.Clone('#payment-option-form-new-cc'));
    PromoteFormBodyIDs($el);
    state.initCCInputs($el);
    SetupBillingAddress(state, $el);
    state.initPaymentTypeSelect(models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_CREDIT, $el);
    BindBackLink($el, back);

    MountCreditCard(state, $el);

    const $ccExp = $el.querySelector<HTMLInputElement>('.cc-expiration');
    $ccExp?.addEventListener('input', () => {
        setTimeout(() => applyExpiration(state.payment, $ccExp));
    });
    BindSetDefault(state.payment, $el);
}

// BindSetDefault copies the Set as Default switch onto the payment. The id is
// unique per form, so the switch is the `.modal-set-default` class.
export function BindSetDefault(payment: Payment, $el: HTMLElement): void {
    const $setDefault = $el.querySelector<HTMLInputElement>('.modal-set-default');
    if (!$setDefault) {
        return;
    }
    $setDefault.checked = Boolean(payment.setDefault);
    $setDefault.addEventListener('change', () => {
        payment.setDefault = $setDefault.checked;
    });
}

// MountCreditCard binds a BlockChyp iframe pair to the `.cc-number` / `.cc-cvc`
// mount <div>s inside $el and stores it on state.creditCardMount so
// CreatePaymentOption / replacePaymentOption can tokenize it. Any previously
// active mount (a prior credit/edit form) is unloaded first. The mount <div>s
// carry the unique id PromoteFormBodyIDs assigned (e.g. `cc-number-<uuid>`),
// which BlockChyp binds to via getElementById.
// keep leaves state.creditCardMount alone so a split row can mount beside
// the single-card form's mount. The single-card path omits it.
export function MountCreditCard(state: State, $el: HTMLElement, opts?: { keep?: boolean }): blockchyp.Mount | undefined {
    const $ccNumber = $el.querySelector<HTMLElement>('.cc-number');
    const $ccCvc = $el.querySelector<HTMLElement>('.cc-cvc');
    // Not $el: it also holds the picker and Back button, the way out on failure.
    const $cardFields = $el.querySelector<HTMLFieldSetElement>('.cc-card-fields');
    if (!$ccNumber || !$ccCvc || !$cardFields) {
        return undefined;
    }

    if (!opts?.keep) {
        state.creditCardMount?.unload();
    }
    const mount = new blockchyp.Mount({
        webPaymentsToken: ResolveMerchantToken(state),
        numberElID: $ccNumber.id,
        cvvElID: $ccCvc.id,
        fieldStyle: PlusCardFieldStyle,
        cardFieldsContainer: $cardFields,
        alternatePayments: AlternatePaymentNames(),
        onValidation: data => SyncCardIcon($el, data.cardType),
        onFieldEvent: BlockChypFieldRing($ccNumber, $ccCvc),
    });
    if (!opts?.keep) {
        state.creditCardMount = mount;
    }
    // Drop the dead mount so the next MountCreditCard rebuilds.
    void mount.ready.catch(() => {
        mount.unload();
        if (state.creditCardMount === mount) {
            state.creditCardMount = undefined;
        }
    });
    return mount;
}

// RefillCreditFields re-populates the still-native expiration from
// state.payment.creditCard — used when resuming an in-progress card entry
// after a failed confirm. The PAN/CVC live in the cross-origin iframe and
// can't be restored, so the customer re-enters them into the fresh mount.
export function RefillCreditFields(p: Payment, $el?: HTMLElement): void {
    const $ccExp = $el?.querySelector<HTMLInputElement>('.cc-expiration');
    if (!$ccExp) {
        return;
    }
    $ccExp.value = `${p.creditCard?.expireMonth || ''}/${p.creditCard?.expireYear || ''}`;
    $ccExp.dispatchEvent(new Event('input'));
}

export interface CreditCardFields {
    expireMonth?: number;
    expireYear?: number;
}

// ReadCreditCardFields pulls the current expiration from the edit form for the
// NewPaymentOption replacement call. The PAN/CVC are tokenized via the
// BlockChyp mount, not read here.
export function ReadCreditCardFields($scope: HTMLElement | null | undefined): CreditCardFields {
    const $ccExp = $scope?.querySelector<HTMLInputElement>('.cc-expiration');
    if (!$ccExp) {
        return {};
    }

    const expVal = $ccExp.value;
    let expireMonth: number | undefined;
    let expireYear: number | undefined;
    if (expVal) {
        const [monthStr, yearStr] = expVal.split('/');
        const month = parseInt(monthStr, 10);
        const year = parseInt(yearStr, 10);
        const currentYear = parseInt(new Date().getFullYear().toString().slice(2), 10);
        if (expVal.length >= 5) {
            const inRange = 1 <= month && month <= 12 && year >= currentYear;
            const expiredThisYear = year === currentYear && month < new Date().getMonth() + 1;
            $ccExp.setCustomValidity(inRange && !expiredThisYear ? '' : 'Invalid expiration date');
        }
        ReportValidity($ccExp);
        expireMonth = month;
        expireYear = year;
    }
    return { expireMonth, expireYear };
}

function applyExpiration(p: Payment, $ccExp: HTMLInputElement): void {
    const val = $ccExp.value;
    if (!val) {
        return;
    }
    const cc = EnsureCreditCard(p);
    const [monthStr, yearStr] = val.split('/');
    const month = parseInt(monthStr, 10);
    const year = parseInt(yearStr, 10);
    const currentYear = parseInt(new Date().getFullYear().toString().slice(2), 10);
    if (val.length >= 5) {
        const inRange = 1 <= month && month <= 12 && year >= currentYear;
        const expiredThisYear = year === currentYear && month < new Date().getMonth() + 1;
        $ccExp.setCustomValidity(inRange && !expiredThisYear ? '' : 'Invalid expiration date');
    }
    ReportValidity($ccExp);
    cc.expireMonth = month;
    cc.expireYear = year;
}
